import { useState, useEffect } from 'react';
import { Minus, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface CounterNumberInputProps {
  value?: number;
  onChange?: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  className?: string;
  showButtons?: boolean;
  /** "lg" makes the +/- buttons bigger for easier mobile tapping. */
  buttonSize?: 'default' | 'lg';
}

export const CounterNumberInput: React.FC<CounterNumberInputProps> = ({
  value = 1,
  onChange,
  min = 1,
  max,
  step = 1,
  disabled = false,
  className,
  showButtons = true,
  buttonSize = 'default',
}) => {
  const [count, setCount] = useState<number>(value);

  // Keep local state in sync with form value
  useEffect(() => {
    setCount(value);
  }, [value]);

  const handleDecrement = () => {
    const newValue = Math.max(min, count - step);
    setCount(newValue);
    onChange?.(newValue);
  };

  const handleIncrement = () => {
    const newValue =
      max !== undefined ? Math.min(max, count + step) : count + step;
    setCount(newValue);
    onChange?.(newValue);
  };

  const isDecrementDisabled = disabled || count <= min;
  const isIncrementDisabled = disabled || (max !== undefined && count >= max);

  const btnSize =
    buttonSize === 'lg' ? 'h-12 w-12 rounded-lg' : 'h-8 w-8 rounded-md';
  const iconSize = buttonSize === 'lg' ? 'h-6 w-6' : 'h-4 w-4';

  return (
    <div className={cn('flex items-center justify-center', className)}>
      <div className="flex border-2 border-neutral-200 dark:border-0 items-center justify-center bg-background dark:bg-neutral-900 rounded-lg px-2 py-2 space-x-2">
        {showButtons && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleDecrement}
            disabled={isDecrementDisabled}
            className={cn(
              btnSize,
              'bg-neutral-200 dark:bg-neutral-800 hover:bg-red-500 dark:hover:bg-red-500 text-gray-600 dark:text-gray-300 transition-colors duration-200'
            )}
          >
            <Minus className={iconSize} />
          </Button>
        )}
        <span className="text-xl text-gray-900 dark:text-gray-100 mx-4 min-w-[2ch] text-center">
          {count}
        </span>
        {showButtons && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleIncrement}
            disabled={isIncrementDisabled}
            className={cn(
              btnSize,
              'bg-green-200 dark:bg-white/80 hover:bg-green-500 dark:hover:bg-green-700 text-black transition-colors duration-200'
            )}
          >
            <Plus className={iconSize} />
          </Button>
        )}
      </div>
    </div>
  );
};
